'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faCheckCircle, faGlobe, faAward, faChartBar, faChartPie } from '@fortawesome/free-solid-svg-icons';
import { siteConfig } from '@/config/site';
import { useUnit } from '@/context/UnitContext';
import axios from 'axios';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Updated accreditation data from July 2025
const accreditationData = [
  { name: 'Baik Sekali', nameEn: 'Excellent', value: 32, color: '#006633' },
  { name: 'Unggul', nameEn: 'Superior', value: 27, color: '#10B981' },
  { name: 'Baik', nameEn: 'Good', value: 19, color: '#FFCC00' },
  { name: 'B', nameEn: 'B', value: 18, color: '#60A5FA' },
  { name: 'Internasional', nameEn: 'International', value: 7, color: '#8B5CF6' },
  { name: 'A', nameEn: 'A', value: 4, color: '#F59E0B' },
  { name: 'Sementara', nameEn: 'Temporary', value: 2, color: '#9CA3AF' },
];

// Summary stats
const summaryStats = [
  { labelId: 'Total Prodi', labelEn: 'Total Programs', value: '109', icon: faBook },
  { labelId: 'Terakreditasi', labelEn: 'Accredited', value: '107', icon: faCheckCircle },
  { labelId: 'Internasional', labelEn: 'International', value: '7', icon: faGlobe },
  { labelId: 'Akreditasi A/Unggul', labelEn: 'A/Superior Rating', value: '31', icon: faAward },
];

// Pie data for visual
const pieData = [
  { name: 'Unggul/A', value: 31, color: '#006633' },
  { name: 'Baik Sekali', value: 32, color: '#10B981' },
  { name: 'Baik/B', value: 37, color: '#FFCC00' },
  { name: 'Lainnya', value: 9, color: '#9CA3AF' },
];

export default function AccreditationSection() {
  const { language } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const [unitInfo, setUnitInfo] = useState<any>(null);
  const webType = siteConfig.webType || '0';
  const isUniversity = webType === '0';
  const isProdi = webType === '2';
  const siteName = siteConfig.name || "UNMUL";
  const siteNameEnglish = siteConfig.nameEnglish || "Mulawarman University";
  const currentSiteName = language === 'en' ? siteNameEnglish : siteName;

  useEffect(() => {
    setIsMounted(true);
    
    // Fetch unit info for Prodi sites
    if (isProdi) {
      const fetchUnitInfo = async () => {
        try {
          const response = await axios.get('/api/unit-info');
          setUnitInfo(response.data);
        } catch (error) {
          console.error('Error fetching unit info:', error);
        }
      };
      fetchUnitInfo();
    }
  }, [isProdi]);

  // Chart.js data for horizontal bar chart
  const barChartData = {
    labels: accreditationData.map(d => language === 'en' ? d.nameEn : d.name),
    datasets: [{
      data: accreditationData.map(d => d.value),
      backgroundColor: accreditationData.map(d => d.color),
      borderRadius: 8,
      barThickness: 24,
    }]
  };

  const barChartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw} ${language === 'en' ? 'programs' : 'prodi'}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6B7280', font: { size: 12 } }
      },
      y: {
        grid: { display: false },
        ticks: { color: '#374151', font: { size: 11 } }
      }
    }
  };

  // Chart.js data for doughnut chart
  const doughnutChartData = {
    labels: pieData.map(d => d.name),
    datasets: [{
      data: pieData.map(d => d.value),
      backgroundColor: pieData.map(d => d.color),
      borderWidth: 2,
      borderColor: '#ffffff',
    }]
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(0);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12" data-aos="fade-up">
          <span className="inline-block bg-unmulYellow text-unmulDarkGreen px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            {language === 'en' ? 'Accreditation' : 'Akreditasi'}
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-800">
            {language === 'en' ? 'Quality Assurance' : 'Penjaminan Mutu'}
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
             {language === 'en' 
              ? `Committed to maintaining high quality standards for ${currentSiteName}` 
              : `Berkomitmen untuk menjaga standar kualitas tinggi bagi ${currentSiteName}`}
          </p>
        </div>

        {isUniversity ? (
           <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {summaryStats.map((stat, index) => (
                <div 
                  key={stat.labelId}
                  className="bg-gradient-to-br from-unmulGreen to-unmulDarkGreen rounded-2xl p-6 text-center text-white shadow-lg"
                  data-aos="fade-up" 
                  data-aos-delay={index * 100}
                >
                  <div className="w-12 h-12 bg-white/20 rounded-xl mx-auto mb-3 flex items-center justify-center">
                    <FontAwesomeIcon icon={stat.icon} className="text-xl text-unmulYellow" />
                  </div>
                  <div className="text-3xl font-bold mb-1">{stat.value}</div>
                  <p className="text-white/80 text-sm">{language === 'en' ? stat.labelEn : stat.labelId}</p>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Bar Chart */}
              <div className="bg-gray-50 rounded-2xl p-6 shadow-lg border border-gray-100 overflow-hidden" data-aos="fade-right">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-unmulGreen/10 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faChartBar} className="text-unmulGreen text-sm" />
                  </span>
                  {language === 'en' ? 'Accreditation Distribution' : 'Distribusi Akreditasi'}
                </h3>
                <div className="h-80 overflow-hidden">
                  {isMounted && <Bar data={barChartData} options={barChartOptions} />}
                </div>
              </div>

              {/* Doughnut Chart */}
              <div className="bg-gray-50 rounded-2xl p-6 shadow-lg border border-gray-100 overflow-hidden" data-aos="fade-left">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-unmulYellow/20 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faChartPie} className="text-unmulYellow text-sm" />
                  </span>
                  {language === 'en' ? 'Quality Composition' : 'Komposisi Kualitas'}
                </h3>
                <div className="h-64 overflow-hidden">
                 {isMounted && <Doughnut data={doughnutChartData} options={doughnutChartOptions} />}
                </div>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                  {pieData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-xs text-gray-600">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
           </>
        ) : isProdi && unitInfo?.program_studi?.prodiakreditasi ? (
          <div className="bg-gradient-to-br from-unmulGreen to-unmulDarkGreen rounded-3xl p-12 text-center shadow-2xl" data-aos="fade-up">
             <div className="w-24 h-24 bg-white/20 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-5xl animate-pulse">
               <FontAwesomeIcon icon={faAward} />
             </div>
             <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
               {language === 'en' ? 'Accreditation' : 'Akreditasi'}
             </h3>
             <div className="inline-block bg-white/90 backdrop-blur-sm px-8 py-4 rounded-2xl shadow-xl mb-6">
               <p className="text-5xl md:text-6xl font-bold text-unmulGreen">
                 {unitInfo.program_studi.prodiakreditasi}
               </p>
             </div>
             <p className="text-white/90 max-w-2xl mx-auto leading-relaxed text-lg">
               {language === 'en'
                 ? `Our study program has been accredited with a rating of ${unitInfo.program_studi.prodiakreditasi}, demonstrating our commitment to quality education and national standards.`
                 : `Program studi kami telah terakreditasi dengan peringkat ${unitInfo.program_studi.prodiakreditasi}, menunjukkan komitmen kami terhadap pendidikan berkualitas dan standar nasional.`
               }
             </p>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 text-center border dashed border-gray-200 shadow-sm" data-aos="fade-up">
             <div className="w-24 h-24 bg-unmulGreen/10 text-unmulGreen rounded-full flex items-center justify-center mx-auto mb-6 text-4xl animate-pulse">
               <FontAwesomeIcon icon={faAward} />
             </div>
             <h3 className="text-2xl font-bold text-gray-800 mb-4">
               {language === 'en' ? 'Our Quality Commitment' : 'Komitmen Mutu Kami'}
             </h3>
             <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
               {language === 'en'
                 ? `We are dedicated to providing excellent education and services. All our programs strictly adhere to national quality standards to ensure the best learning outcomes for our students.`
                 : `Kami berdedikasi untuk memberikan layanan dan pendidikan yang unggul. Seluruh program kami secara ketat mematuhi standar kualitas nasional untuk memastikan hasil pembelajaran terbaik bagi mahasiswa kami.`
               }
             </p>
          </div>
        )}
      </div>
    </section>
  );
}
